import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, CalendarClock, Phone, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const SESSION_KEY = "om_payment_alert_shown_v1";

function inr(n) {
  return `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function Section({ title, items, icon: Icon, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${tone}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.slice(0, 4).map((p) => {
          const digits = (p.mobile || "").replace(/[^0-9]/g, "");
          return (
            <li key={p.id} className="flex items-center justify-between gap-2 border-l-2 border-zinc-200 pl-3 py-1">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{p.customer_name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {p.product_name}
                  {(p.remaining_gold || 0) > 0 && ` · ${p.remaining_gold.toFixed(2)}g Au`}
                  {(p.remaining_silver || 0) > 0 && ` · ${p.remaining_silver.toFixed(2)}g Ag`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm num font-semibold">{inr(p.remaining_balance)}</div>
                {digits && (
                  <a href={`tel:${digits}`} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Call
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function PaymentAlertDialog() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user || user === false) return;
    // Show only once per login session, only for Order-side roles
    const role = user.role;
    if (!["admin", "order_staff", "staff"].includes(role)) return;
    const shownFor = sessionStorage.getItem(SESSION_KEY);
    if (shownFor === user.email) return;
    api.get("/payments/alerts").then((r) => {
      if (r.data?.has_alerts) {
        setData(r.data);
        setOpen(true);
        sessionStorage.setItem(SESSION_KEY, user.email);
      } else {
        sessionStorage.setItem(SESSION_KEY, user.email);
      }
    }).catch(() => {});
  }, [user]);

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-alert-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> Payment Reminder
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Section title="Today" items={data.due_today} icon={Clock} tone="text-amber-700" />
          <Section title="Tomorrow" items={data.due_tomorrow} icon={CalendarClock} tone="text-blue-700" />
          <Section title="Overdue" items={data.overdue} icon={AlertTriangle} tone="text-red-700" />
        </div>
        <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
          <Button onClick={() => { setOpen(false); nav("/payments"); }} className="bg-zinc-900 hover:bg-zinc-800 flex-1" data-testid="open-payments-btn">
            Open Payments <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="remind-later-btn">
            Remind Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
